import React, { useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import Card from "@mui/material/Card";
import { Typography, Box, CircularProgress, Dialog, DialogContent } from "@mui/material";
import {
  StyledButton,
  StyledAlert,
  StyledSnackbar,
  StyledAgGridContainer,
} from "components/StyledComponents/styledComponents.js";
import PropTypes from "prop-types";
import Cookie from "js-cookie";
import URLs from "constants/urls";
import VisibilityIcon from "@mui/icons-material/Visibility";

export default function LetterGenerater() {
  const [rowData, setRowData] = useState([]);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const hrms_token = Cookie.get("hrms_token");

  useEffect(() => {
    fetchApplicantData();
  }, []);

  const fetchApplicantData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${URLs.hrmsGetAllApplicants}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch applicant data");
      }

      const data = await response.json();
      const formattedData = data.map((applicant) => ({
        applicantName: `${applicant.firstName || ""} ${applicant.lastName || ""}`.trim(),
        dateOfJoining: applicant.dateOfJoining
          ? new Date(applicant.dateOfJoining).toLocaleDateString()
          : "-",
        ctcPerYear: applicant.ctcPerYear
          ? `₹${Number(applicant.ctcPerYear).toLocaleString("en-IN")}`
          : "-",
        emailId: applicant.emailId || "-",
        id: applicant.applicantId,
      }));

      setRowData(formattedData);
    } catch (error) {
      console.error("Error fetching applicant data:", error);
      setAlert("Failed to fetch applicant data", "error");
    } finally {
      setLoading(false);
    }
  };

  const ViewLetterRenderer = (props) => (
    <div style={{ cursor: "pointer", display: "flex", justifyContent: "center" }}>
      <VisibilityIcon
        onClick={() => handleLetterGeneration(props.data)}
        style={{ color: "#1a73e8" }}
      />
    </div>
  );

  ViewLetterRenderer.propTypes = {
    data: PropTypes.object.isRequired,
  };

  const columnDefs = [
    {
      field: "applicantName",
      headerName: "Applicant Name",
      flex: 1.5,
      cellStyle: {
        textAlign: "left",
        paddingLeft: "20px",
        color: "#333333",
        fontSize: "14px",
      },
      headerClass: "custom-header",
    },
    {
      field: "dateOfJoining",
      headerName: "Date of Joining",
      flex: 1,
      cellStyle: {
        textAlign: "left",
        paddingLeft: "20px",
        color: "#333333",
        fontSize: "14px",
      },
      headerClass: "custom-header",
    },
    {
      field: "ctcPerYear",
      headerName: "CTC Per Year",
      flex: 1,
      cellStyle: {
        textAlign: "left",
        paddingLeft: "20px",
        color: "#333333",
        fontSize: "14px",
      },
      headerClass: "custom-header",
    },
    {
      field: "emailId",
      headerName: "Email ID",
      flex: 1.5,
      cellStyle: {
        textAlign: "left",
        paddingLeft: "20px",
        color: "#333333",
        fontSize: "14px",
      },
      headerClass: "custom-header",
    },
    {
      headerName: "View Letter",
      field: "view",
      cellRenderer: ViewLetterRenderer,
      width: 120,
      headerClass: "custom-header",
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    suppressMovable: true,
  };

  const handleLetterGeneration = async (applicant) => {
    try {
      setLoading(true);
      const applicantId = applicant.id;

      if (!hrms_token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const response = await fetch(`${URLs.hrmsGetOfferLetterPrint}/${applicantId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Session expired. Please login again.");
        }
        const errorText = await response.text();
        console.error("API Error:", {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
        });
        throw new Error(errorText || "Failed to generate offer letter");
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error("Generated PDF is empty");
      }

      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setDialogOpen(true);

      setAlert("Offer letter generated successfully", "success");
    } catch (error) {
      console.error("Error generating offer letter:", error);
      setAlert(error.message || "Failed to generate offer letter. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "offer_letter.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ height: "calc(100vh - 120px)", pt: 1, pb: 1 }}>
        <Card sx={{ height: "100%" }}>
          <MDBox p={3} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {loading ? (
              <Box
                sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <StyledAgGridContainer className="ag-theme-alpine" sx={{ flex: 1, width: "100%" }}>
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  animateRows={true}
                  pagination={true}
                  paginationPageSize={15}
                  rowHeight={48}
                  headerHeight={48}
                  suppressCellSelection={true}
                />
              </StyledAgGridContainer>
            )}
          </MDBox>
        </Card>
      </Box>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogContent>
          {pdfUrl && (
            <div style={{ height: "80vh", display: "flex", flexDirection: "column" }}>
              <iframe
                src={pdfUrl}
                style={{ width: "100%", height: "100%", border: "none" }}
                title="Offer Letter Preview"
              />
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                <StyledButton onClick={handleDownload} variant="contained">
                  Download
                </StyledButton>
              </Box>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>
    </DashboardLayout>
  );
}
